import { Button } from "@ui/Button";
import {
	PdfDocumentView,
	type PdfDocumentViewHandle,
} from "@ui/molecules/PdfDocumentView/PdfDocumentView";
import { ThumbnailTrackCaption } from "@ui/molecules/ThumbnailTrack/ThumbnailTrack";
import { Stack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { createEffect, createSignal, on, Show } from "solid-js";
import {
	ChatCardToolbar,
	ChatCardToolbarActionsProvider,
} from "../../chat-panel/components/ChatCardToolbar";
import {
	type DocumentPreviewPanelContent,
	emitDocumentPreviewPageShown,
} from "../documentPanelEvents";
import "./DocumentPanelContent.css";

type Props = {
	content: DocumentPreviewPanelContent;
	onClose: () => void;
};

/**
 * Contents of the slide-out pane when a document page is opened from the chat:
 * a caption matching the artifact's own thumbnail strip, the document's summary
 * under it, the document in a {@link PdfDocumentView} opened at that page, and
 * beneath it the toolbar of the card it came from, so the artifact can be
 * saved, downloaded and rated from where it is being read.
 *
 * The caption follows the reader from there, because the pane shows the whole
 * document and the page it opened at is only where the reading started.
 *
 * The pane is the widest surface in the conversation - past 800px it takes the
 * whole viewport bar the chat column - so the viewer is sized from the pane's
 * height rather than a fixed length, and the pages are scaled to the pane's
 * full width.
 */
export const DocumentPanelContent = (props: Props) => {
	const [page, setPage] = createSignal(props.content.page);
	let viewer: PdfDocumentViewHandle | undefined;

	/** The caption's page, and the card's thumbnail strip, follow the reading. */
	const showPage = (pageNumber: number) => {
		setPage(pageNumber);
		emitDocumentPreviewPageShown({ url: props.content.url, page: pageNumber });
	};

	createEffect(
		on(
			() => props.content,
			(content) => {
				// Every thumbnail opens the pane afresh, including the one the pane
				// is already showing, which the reader may have scrolled away from.
				setPage(content.page);
				viewer?.goToPage(content.page);
			},
			{ defer: true },
		),
	);

	return (
		<section class="chat-document-pane" aria-label="Document preview">
			<Stack direction="row" align="center" spacing="medium">
				<Stack grow>
					<ThumbnailTrackCaption
						title={props.content.title}
						selectedIndex={page() - 1}
						total={props.content.totalPages}
					/>
				</Stack>
				<Button
					variant="text"
					symbol="close"
					aria-label="Close document preview"
					onClick={props.onClose}
				/>
			</Stack>
			<Show when={props.content.summary}>
				{(summary) => (
					<Text class="chat-document-pane__summary" size="standard" color="fg2">
						{summary()}
					</Text>
				)}
			</Show>
			<div class="chat-document-pane__viewer">
				<PdfDocumentView
					url={props.content.url}
					page={props.content.page}
					height="100%"
					label={`${props.content.title}, ${props.content.totalPages} pages`}
					onPageChange={showPage}
					handle={(handle) => {
						viewer = handle;
					}}
				/>
			</div>
			<Show when={props.content.toolbar}>
				{(toolbar) => (
					<ChatCardToolbarActionsProvider
						save={props.content.toolbarActions?.save}
						rate={props.content.toolbarActions?.rate}
					>
						<ChatCardToolbar {...toolbar()} />
					</ChatCardToolbarActionsProvider>
				)}
			</Show>
		</section>
	);
};
